import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wallet, ArrowLeft, Loader2, CreditCard, Shield } from 'lucide-react';
import { publicApi } from '@/lib/api';

const QUICK_AMOUNTS = [100, 200, 500, 1000];

const WalletTopUp = () => {
  const { token } = useParams();
  const [balance, setBalance] = useState(null);
  const [attendeeName, setAttendeeName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState('');
  const [initiating, setInitiating] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [walletId, setWalletId] = useState(null);

  useEffect(() => {
    const fetchWallet = async () => {
      try {
        const response = await publicApi.get(`/wallet/${token}`);
        const data = response.data;
        setBalance(data.balance);
        setAttendeeName(data.attendee_name);
        setCampaignName(data.campaign_name);
        setWalletId(data.wallet_id);
      } catch (err) {
        if (err.response) {
          setError(err.response.data?.detail || 'Wallet not found');
        } else {
          setError('Unable to connect to server. Please check your connection and try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchWallet();
  }, [token]);

  const formatCurrency = (amt) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);
  };

  const handleTopUp = async () => {
    const topUpAmount = parseFloat(amount);
    if (isNaN(topUpAmount) || topUpAmount <= 0) return;
    if (topUpAmount > 10000) {
      setPaymentError('Maximum top-up amount is ₹10,000');
      return;
    }

    setInitiating(true);
    setPaymentError(null);
    try {
      const response = await publicApi.post('/public/wallet-topup', { token, amount: topUpAmount });
      const result = response.data;
      // Validate payment_url to prevent open redirect attacks
      try {
        const paymentUrl = new URL(result.payment_url);
        const allowedHosts = ['www.instamojo.com', 'instamojo.com', 'test.instamojo.com', 'api.instamojo.com'];
        if (!allowedHosts.includes(paymentUrl.hostname)) {
          setPaymentError('Invalid payment URL received. Please contact support.');
          setInitiating(false);
          return;
        }
        window.location.href = result.payment_url;
      } catch {
        setPaymentError('Invalid payment URL received. Please try again.');
        setInitiating(false);
        return;
      }
    } catch (err) {
      if (err.response) {
        setPaymentError(err.response.data?.detail || 'Payment initiation failed. Please try again.');
      } else {
        setPaymentError(`Unable to reach payment server: ${err.message}`);
      }
      setInitiating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full">
          <CardContent className="p-8 text-center">
            <Wallet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Wallet Not Found</h2>
            <p className="text-gray-500">This wallet link may be invalid or expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm p-4">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to={`/wallet/${token}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <p className="text-sm text-gray-500">{campaignName}</p>
            <h1 className="font-bold text-lg">Top Up Wallet</h1>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-6">
        {/* Current Balance */}
        <Card>
          <CardContent className="p-4 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Current Balance</p>
              <p className="text-sm font-medium">{attendeeName}</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(balance)}</p>
          </CardContent>
        </Card>

        {/* Quick Amount Buttons */}
        <div>
          <p className="text-sm text-gray-500 mb-3">Select Amount</p>
          <div className="grid grid-cols-4 gap-3">
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(String(amt))}
                className={`p-3 rounded-lg border-2 text-center font-semibold transition-colors ${
                  amount === String(amt)
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {formatCurrency(amt)}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Amount */}
        <div>
          <p className="text-sm text-gray-500 mb-2">Or enter custom amount</p>
          <Input
            type="number"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            className="text-xl h-14 text-center font-bold"
          />
        </div>

        {/* Pay Button */}
        <Button
          onClick={handleTopUp}
          disabled={!amount || parseFloat(amount) <= 0 || parseFloat(amount) > 10000 || initiating}
          className="w-full h-14 text-lg"
          size="lg"
        >
          {initiating ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <CreditCard className="h-5 w-5 mr-2" />
          )}
          {amount ? `Pay ${formatCurrency(parseFloat(amount))}` : 'Select Amount'}
        </Button>

        {paymentError && (
          <p className="text-sm text-center text-red-600">{paymentError}</p>
        )}

        <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-1">
          <Shield className="h-3 w-3" />
          Secure payments powered by Instamojo. You will be redirected to complete payment.
        </p>
      </div>
    </div>
  );
};

export default WalletTopUp;
